import type { Coding, Extension, Identifier, Practitioner } from '@medplum/fhirtypes';
import {
  CREDENTIAL_TYPE_SYSTEM,
  QUALIFICATION_EXT_URLS,
} from '@unify/fhir-model/constants';
import { getCredentials } from '@unify/fhir-model/resources/practitioner';
import { LICENSE_TYPES } from '../pages/onboarding/constants';

type PractitionerQualification = NonNullable<Practitioner['qualification']>[number];

const LICENSE_TYPE_ABBREVIATIONS: Record<string, string> = {
  lcsw: 'LCSW',
  lpc: 'LPC',
  lpcc: 'LPCC',
  lmft: 'LMFT',
  'clinical-psychologist': 'Psychologist',
  psychiatrist: 'MD',
  'psychiatric-np': 'PMHNP',
};

function getQualificationCode(qualification: PractitionerQualification): string | undefined {
  return qualification.code?.coding?.find(
    (coding: Coding) => coding.system === CREDENTIAL_TYPE_SYSTEM,
  )?.code;
}

function getQualificationExtension(qualification: PractitionerQualification, url: string) {
  return qualification.extension?.find((extension: Extension) => extension.url === url);
}

function getQualificationString(qualification: PractitionerQualification, url: string): string | undefined {
  const extension = getQualificationExtension(qualification, url);
  return extension?.valueString ?? extension?.valueCode;
}

function getQualificationBoolean(qualification: PractitionerQualification, url: string): boolean | undefined {
  return getQualificationExtension(qualification, url)?.valueBoolean;
}

function getLicenseIdentifierValue(qualification: PractitionerQualification): string | undefined {
  const value =
    qualification.identifier?.find(
      (identifier: Identifier) =>
        identifier.system !== 'https://unify.health/fhir/salesforce-id' &&
        identifier.system !== 'https://unify.health/fhir/verifiable-id',
    )?.value?.trim();
  return value || undefined;
}

function isLicenseQualification(qualification: PractitionerQualification): boolean {
  return getQualificationCode(qualification) === 'license';
}

function isActiveLicense(qualification: PractitionerQualification): boolean {
  const status = getQualificationString(qualification, QUALIFICATION_EXT_URLS.licenseStatus);
  if (status?.toLowerCase() === 'inactive') return false;
  if (getQualificationBoolean(qualification, QUALIFICATION_EXT_URLS.superseded)) return false;
  return true;
}

function licenseAbbreviation(qualification: PractitionerQualification): string | undefined {
  const licenseType = getQualificationString(qualification, QUALIFICATION_EXT_URLS.licenseType);
  if (licenseType) {
    const mapped = LICENSE_TYPE_ABBREVIATIONS[licenseType];
    if (mapped) return mapped;
    const fromCatalog = LICENSE_TYPES.find((entry) => entry.value === licenseType)?.label;
    if (fromCatalog && fromCatalog.length <= 12) return fromCatalog;
  }

  const licenseName = qualification.code?.text?.trim();
  if (!licenseName) return undefined;
  if (licenseName.length <= 12 && !licenseName.includes(' ')) return licenseName;
  return undefined;
}

/** Format one license qualification for signature display — e.g. "LMFT #123456". */
export function formatLicenseQualification(
  qualification: PractitionerQualification,
): string | undefined {
  const abbrev = licenseAbbreviation(qualification);
  const licenseNumber = getLicenseIdentifierValue(qualification);
  if (abbrev && licenseNumber) return `${abbrev} #${licenseNumber}`;
  if (abbrev) return abbrev;
  if (licenseNumber) return `#${licenseNumber}`;
  return qualification.code?.text?.trim() || undefined;
}

export function getPrimaryLicenseQualification(
  practitioner: Practitioner | undefined,
): PractitionerQualification | undefined {
  if (!practitioner?.qualification?.length) return undefined;
  const licenses = practitioner.qualification.filter(isLicenseQualification);
  const active = licenses.filter(isActiveLicense);
  const pool = active.length > 0 ? active : licenses;
  const primary = pool.find(
    (qualification) =>
      getQualificationBoolean(qualification, QUALIFICATION_EXT_URLS.isPrimary) === true,
  );
  return primary ?? pool[0];
}

export interface PractitionerLicenseOption {
  /** Stable key for MultiSelect (`qualification.id` or index fallback). */
  key: string;
  /** Signature display string, e.g. "LMFT #123456". */
  label: string;
  jurisdictionState?: string;
}

export function formatPractitionerLicenseOption(option: PractitionerLicenseOption): string {
  return option.jurisdictionState ? `${option.label} (${option.jurisdictionState})` : option.label;
}

/**
 * Active (or all) license qualifications as MultiSelect options.
 * Prefers active licenses; falls back to all license-type qualifications.
 */
export function listPractitionerLicenseOptions(
  practitioner: Practitioner | undefined,
): PractitionerLicenseOption[] {
  if (!practitioner?.qualification?.length) return [];
  const licenses = practitioner.qualification
    .map((qualification, index) => ({ qualification, index }))
    .filter(({ qualification }) => isLicenseQualification(qualification));
  const active = licenses.filter(({ qualification }) => isActiveLicense(qualification));
  const pool = active.length > 0 ? active : licenses;
  const primary = pool.find(
    ({ qualification }) =>
      getQualificationBoolean(qualification, QUALIFICATION_EXT_URLS.isPrimary) === true,
  );
  const ordered = primary
    ? [primary, ...pool.filter((entry) => entry !== primary)]
    : pool;

  return ordered.flatMap(({ qualification, index }) => {
    const label = formatLicenseQualification(qualification);
    if (!label) return [];
    const jurisdiction =
      getQualificationString(qualification, QUALIFICATION_EXT_URLS.jurisdictionState)?.trim();
    return [
      {
        key: qualification.id?.trim() || `license-${index}`,
        label,
        jurisdictionState: jurisdiction ? jurisdiction.toUpperCase() : undefined,
      },
    ];
  });
}

/**
 * Default-selected license keys: prefer jurisdiction match to client/appointment state,
 * else the practitioner's primary (or first) license.
 */
export function selectDefaultLicenseKeys(
  options: PractitionerLicenseOption[],
  jurisdictionState?: string,
): string[] {
  if (options.length === 0) return [];
  const normalized = jurisdictionState?.trim().toUpperCase();
  if (normalized) {
    const matched = options.filter((option) => option.jurisdictionState === normalized);
    if (matched.length > 0) return matched.map((option) => option.key);
  }
  return [options[0].key];
}

/** Join selected license labels for the signature credentials line. */
export function formatSelectedLicenseCredentials(
  options: PractitionerLicenseOption[],
  selectedKeys: string[],
): string | undefined {
  if (selectedKeys.length === 0) return undefined;
  const byKey = new Map(options.map((option) => [option.key, formatPractitionerLicenseOption(option)]));
  const labels = selectedKeys
    .map((key) => byKey.get(key))
    .filter((label): label is string => Boolean(label));
  return labels.length > 0 ? labels.join('\n') : undefined;
}

/** Match both legacy saved labels and jurisdiction-bearing labels. */
export function selectSavedLicenseKeys(
  options: PractitionerLicenseOption[],
  savedLicense: string | undefined,
): string[] {
  if (!savedLicense?.trim()) return [];
  const savedLabels = savedLicense.split(/[,\n]/).map((part) => part.trim());
  return options
    .filter((option) =>
      savedLabels.includes(option.label) || savedLabels.includes(formatPractitionerLicenseOption(option)),
    )
    .map((option) => option.key);
}

/**
 * Display credential for signatures — e.g. "LMFT #123456" from the practitioner's primary license.
 */
export function formatPractitionerLicenseCredential(
  practitioner: Practitioner | undefined,
): string | undefined {
  if (!practitioner) return undefined;

  const qualification = getPrimaryLicenseQualification(practitioner);
  if (!qualification) {
    return getCredentials(practitioner)?.trim() || undefined;
  }

  return formatLicenseQualification(qualification) || getCredentials(practitioner)?.trim() || undefined;
}

export function formatPractitionerDisplayName(practitioner: Practitioner | undefined): string {
  if (!practitioner) return '';
  const name = practitioner.name?.[0];
  return (
    name?.text?.trim() ||
    [name?.given?.join(' '), name?.family].filter(Boolean).join(' ').trim() ||
    ''
  );
}
